import type { ContractChangeStatusEnum, PriceListUerRowData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';

import type { MutableRefObject } from 'react';
import { useCallback, useImperativeHandle, useState } from 'react';

import { AgreementPriceListUerModal } from '../../AgreementPriceListUerModal/AgreementPriceListUerModal';
import { AddAgreementPriceUer } from '../AddAgreementPriceUer';
import { AgreementFormTopPanel } from '../AgreementFormTopPanel';
import { AgreementPriceListTable } from '../AgreementPriceListTable';
import { useGetAgreementUerItems } from '../hooks/useGetAgreementUerItems';
import { useRemoveAgreementsUer } from '../hooks/useRemoveAgreementsUer';

const noDataComp = () => 'Для отображения данных добавьте расценки';

export const AgreementPriceListUer = ({
    agreementId,
    refreshData,
    disabledByStatus,
    infoText = undefined,
    needUpdateCurrentPriceList,
    needUpdatePriceListHandler,
    isDisabledByProjectStatus,
    contractChangeStatus,
    isDisabledByContractStatus,
    rulesAccess,
    agreementTableApiRef,
    hasItemRow,
}: {
    agreementId: number;
    refreshData: () => void;
    disabledByStatus: boolean;
    isDisabledByProjectStatus: boolean;
    isDisabledByContractStatus: boolean;
    infoText?: string;
    needUpdateCurrentPriceList?: boolean;
    contractChangeStatus?: ContractChangeStatusEnum;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
    rulesAccess?: IUseAccess['additionalAgreement'];
    agreementTableApiRef: MutableRefObject<{ refreshCurrentTable: (skipCheckedReset?: boolean) => void } | undefined>;
    hasItemRow: boolean;
}) => {
    const [expandedRowData, setExpandedRowData] = useState<null | PriceListUerRowData>(null);
    const [expandedModalOpen, setExpandedModalOpen] = useState<boolean>(false);

    const handleExpandRow = useCallback((data: PriceListUerRowData) => {
        setExpandedRowData(data);
        setExpandedModalOpen(true);
    }, []);

    const {
        datasource,
        columns,
        columnsForTableSettings,
        requestStatus,
        isColumnVisible,
        handleTableSettingsConfirm,
        rowCount,
        totalCount,
        refreshAgreementUer,
        clearCheckedHandler,
        checkedIds,
        refreshFilters,
        tableRef,
    } = useGetAgreementUerItems({
        contractChangeId: agreementId,
        refreshData,
        disabledByStatus,
        isDisabledByProjectStatus,
        needUpdateCurrentPriceList,
        contractChangeStatus,
        needUpdatePriceListHandler,
        handleExpandRow,
    });

    const handleCloseExpandedRowModal = useCallback(() => {
        setExpandedModalOpen(false);
    }, []);

    useImperativeHandle(agreementTableApiRef, () => ({ refreshCurrentTable: refreshAgreementUer }), [
        refreshAgreementUer,
    ]);

    const {
        removeUerContractChangeData,
        removeUersFromContractChangeErr,
        removeUersFromContractChangeStatus,
        removeUersHandler,
        removeUersContractChangeSetData,
    } = useRemoveAgreementsUer({
        refreshAgreementData: refreshAgreementUer,
        refreshFilters,
        contractChangeId: agreementId,
        checkedIds,
    });

    return (
        <>
            <AgreementFormTopPanel infoText={infoText}>
                <AddAgreementPriceUer
                    disabledByStatus={disabledByStatus}
                    refreshData={refreshAgreementUer}
                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                    isDisabledByContractStatus={isDisabledByContractStatus}
                    refreshFilters={refreshFilters}
                    isDisabledRulesAccess={!rulesAccess?.canEdit}
                />
            </AgreementFormTopPanel>
            <AgreementPriceListTable
                refreshCurrentTableRef={tableRef}
                rowCount={rowCount}
                totalCount={totalCount}
                columnsForTableSettings={columnsForTableSettings}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isColumnVisible}
                datasource={datasource}
                columns={columns}
                requestStatus={requestStatus}
                checkedIdsArr={checkedIds}
                handleClearChecked={clearCheckedHandler}
                removeRowsData={removeUerContractChangeData}
                removeRowsStatus={removeUersFromContractChangeStatus}
                removeRowsError={removeUersFromContractChangeErr}
                removeRowsSetData={removeUersContractChangeSetData}
                handleRemoveRows={removeUersHandler}
                tableContext={{ tableUniqueId: 'agreementUerItems' }}
                emptyDataComponent={noDataComp}
                hasItemRow={hasItemRow}
            />
            {expandedModalOpen && (
                <AgreementPriceListUerModal expandedRowData={expandedRowData} onClose={handleCloseExpandedRowModal} />
            )}
        </>
    );
};

AgreementPriceListUer.displayName = 'AgreementPriceListUer';
